/**
 * PremiereTimeline — a SIMPLE but REAL Premiere-Pro-style, time-based
 * timeline (timeline.md / mockups/timeline-premiere.png). It is the
 * EXPORT surface at the end of a project and the "watch it assemble"
 * spine of the studio.
 *
 * One component, mounted in TWO places (DRY):
 *   - the persistent bottom Timeline dock (`Timeline.ts`), and
 *   - the full-screen `TimelinePanel`.
 *
 * What it does:
 *   - Top toolbar: snap, cut, text, waveform | PLAY + time readout |
 *     zoom-out, zoom-in, grid, EXPORT(download). Plus the global
 *     BACKGROUND SCORE control (Timeline owns it — _GLOBAL.md G8).
 *   - A real TIME RULER in seconds with a draggable playhead/scrubber.
 *   - Horizontal TRACKS (Text/subtitles, SFX, Main filmstrip, Audio
 *     dialogue), each with a label; Main + Audio carry a volume slider
 *     and mute. Clips are real time-positioned blocks: selectable, and
 *     cut/trim/delete-able.
 *
 * LIVING build-up — the render model is DERIVED fresh from the pipeline
 * on every render, so the timeline visibly fills as you work with zero
 * stale state:
 *   1. script     → subtitles (Text)
 *   2. voices     → dialogue audio clips (Audio), once a speaker's voice
 *                   is designed (or the scene's line is generated)
 *   3. storyboard → Main filmstrip blocks
 *   4. final video → Main blocks light up as "rendered"
 *   5. SFX between beats + the overall background score
 *
 * Shared clip model: the derived VIDEO (Main) clips are synced into
 * `store.clips` (deterministic `der_` ids, foreign clips preserved) so
 * Final Video plays back the same arranged cut (final-video.md). The
 * store/score/casting APIs are DEFENSIVELY feature-detected — this
 * worktree may predate them, in which case derivation still drives the
 * UI and the integrator reconciles the real store at merge (G9).
 *
 * Every clip is click-to-edit: it focuses the scene and jumps to the
 * tool that owns it.
 */

import type { PanelManager, StudioPanelId } from "./PanelManager";
import type { StudioScene, StudioStore } from "./StudioStore";

// --- Documented shared models (timeline.md / final-video.md) -----------
// Defined locally as DEFENSIVE types per G9; the integrator's StudioStore
// already exposes these in the foundation lineage.
export interface StudioClip {
  id: string;
  sceneId?: string | null;
  frameId?: string | null;
  startSec: number;
  durationSec: number;
  type: "video" | "audio" | "text" | "subtitle";
  content?: { image?: string | null; waveform?: string | null; text?: string | null };
  ready?: boolean;
}

export interface BackgroundScore {
  style: string;
  intensity: number;
  trackUrl?: string | null;
}

/** Optional store methods we feature-detect (added by the integrator). */
interface StoreShim {
  getBackgroundScore?(): BackgroundScore;
  setBackgroundScore?(patch: Partial<BackgroundScore>): void;
  setClips?(clips: StudioClip[]): void;
  getCastingForScene?(sceneId: string): string[];
}

export interface PremiereTimelineOptions {
  store: StudioStore;
  /** Optional — enables click-to-edit jumps to a clip's source tool. */
  panelManager?: PanelManager;
  /** "dock" is the compact footer tray; "panel" is the full-screen view. */
  variant?: "dock" | "panel";
}

type TrackId = "text" | "sfx" | "main" | "audio";
type ClipKind = "subtitle" | "audio" | "video" | "sfx";

interface ClipBlock {
  id: string;
  startSec: number;
  durationSec: number;
  label: string;
  sceneId: string | null;
  frameId: string | null;
  kind: ClipKind;
  /** Final-video rendered (vs. storyboard placeholder). */
  ready: boolean;
  /** Thumbnail for filmstrip blocks. */
  image: string | null;
  jumpTo: StudioPanelId;
}

interface TrackModel {
  id: TrackId;
  label: string;
  blocks: ClipBlock[];
  /** Show a volume slider + mute toggle (Main + Audio, per the mockup). */
  hasVolume: boolean;
  /** Render a waveform texture (Audio + SFX). */
  waveform: boolean;
}

interface TimelineModel {
  tracks: TrackModel[];
  durationSec: number;
}

/** Track order, top → bottom. */
const TRACK_ORDER: ReadonlyArray<{
  id: TrackId;
  label: string;
  hasVolume: boolean;
  waveform: boolean;
}> = [
  { id: "text", label: "Text", hasVolume: false, waveform: false },
  { id: "sfx", label: "SFX", hasVolume: false, waveform: true },
  { id: "main", label: "Main", hasVolume: true, waveform: false },
  { id: "audio", label: "Audio", hasVolume: true, waveform: true },
];

/** Store slices that, when changed, mean the derived cut must rebuild. */
const PIPELINE_KEYS = [
  "scenes",
  "characters",
  "storyboardFramesBySceneId",
  "storyboardStatus",
  "videoStatus",
] as const;

const LOCAL_SCORE_KEY = "koko_studio_background_score";
const MIN_PX_PER_SEC = 24;
const MAX_PX_PER_SEC = 220;
const DEFAULT_PX_PER_SEC = 90;
const ZOOM_FACTOR = 1.35;
const SNAP_SEC = 0.5;
const SCORE_STYLES = ["cinematic", "lo-fi pulse", "ambient", "tense", "warm strings", "synthwave"];

export class PremiereTimeline {
  private readonly store: StudioStore;
  private readonly shim: StoreShim;
  private readonly panelManager?: PanelManager;
  private readonly variant: "dock" | "panel";

  private readonly root: HTMLElement;
  private rulerEl!: HTMLDivElement;
  private tracksEl!: HTMLDivElement;
  private labelsEl!: HTMLDivElement;
  private scrollEl!: HTMLDivElement;
  private playheadEl!: HTMLDivElement;
  private readoutEl!: HTMLSpanElement;
  private playButton!: HTMLButtonElement;
  private scoreHost: HTMLElement | null = null;

  private unsubscribers: (() => void)[] = [];

  // View state (session-local).
  private pxPerSec = DEFAULT_PX_PER_SEC;
  private playheadSec = 0;
  private playing = false;
  private rafId: number | null = null;
  private lastFrameTs = 0;
  private activeTool: "select" | "cut" | "text" = "select";
  private snap = false;
  private grid = false;
  private showWaveform = true;
  private selectedClipId: string | null = null;
  private readonly mutedTracks = new Set<TrackId>();
  private readonly trackVolume = new Map<TrackId, number>([
    ["main", 80],
    ["audio", 70],
  ]);

  // Edit overlays applied on top of the live derivation.
  private readonly splitPoints = new Map<string, number[]>();
  private readonly trimOverrides = new Map<string, { start: number; dur: number }>();
  private readonly deletedClips = new Set<string>();
  private readonly manualTextBlocks: ClipBlock[] = [];

  // Local background-score fallback (used until the store owns it).
  private localScore: BackgroundScore = { style: "cinematic", intensity: 60, trackUrl: null };

  // Cached model for the current render (hit-testing + summary).
  private model: TimelineModel = { tracks: [], durationSec: 0 };
  private syncing = false;

  constructor(options: PremiereTimelineOptions) {
    this.store = options.store;
    this.shim = options.store as unknown as StoreShim;
    this.panelManager = options.panelManager;
    this.variant = options.variant ?? "panel";
    this.loadLocalScore();
    this.root = this.build();
  }

  /** The element to mount. */
  getElement(): HTMLElement {
    return this.root;
  }

  /** Subscribe + first render. Call once after mounting into the DOM. */
  start(): void {
    for (const key of PIPELINE_KEYS) {
      this.unsubscribers.push(
        this.store.subscribe(key, () => {
          this.syncStoreClips();
          this.render();
        }),
      );
    }
    // Optional slices (present once the integrator wires the shared model).
    this.trySubscribe("clips", () => this.render());
    this.trySubscribe("backgroundScore", () => this.renderScoreControl());
    this.syncStoreClips();
    this.render();
  }

  /** Re-render on demand (e.g. when the dock expands and width is known). */
  refresh(): void {
    this.render();
  }

  destroy(): void {
    this.stopPlayback();
    for (const unsub of this.unsubscribers) unsub();
    this.unsubscribers = [];
  }

  /** Compact summary for the dock's collapsed strip. */
  getSummary(): { clipCount: number; durationSec: number } {
    const clipCount = this.model.tracks.reduce((n, t) => n + t.blocks.length, 0);
    return { clipCount, durationSec: this.model.durationSec };
  }

  private trySubscribe(key: string, fn: () => void): void {
    try {
      this.unsubscribers.push(
        (this.store as unknown as {
          subscribe(k: string, f: () => void): () => void;
        }).subscribe(key, fn),
      );
    } catch {
      /* key not present in this store version — ignore */
    }
  }

  // ------------------------------------------------------------------
  // DOM scaffold
  // ------------------------------------------------------------------

  private build(): HTMLElement {
    const root = document.createElement("div");
    root.className = "pt-root";
    root.dataset.variant = this.variant;
    root.dataset.testid = "premiere-timeline";

    root.appendChild(this.buildToolbar());

    const body = document.createElement("div");
    body.className = "pt-body";

    this.labelsEl = document.createElement("div");
    this.labelsEl.className = "pt-labels";

    this.scrollEl = document.createElement("div");
    this.scrollEl.className = "pt-scroll";

    this.rulerEl = document.createElement("div");
    this.rulerEl.className = "pt-ruler";
    this.rulerEl.dataset.kokoAction = "timeline-scrub";

    this.tracksEl = document.createElement("div");
    this.tracksEl.className = "pt-tracks";

    this.playheadEl = document.createElement("div");
    this.playheadEl.className = "pt-playhead";
    this.playheadEl.dataset.testid = "timeline-playhead";
    const knob = document.createElement("div");
    knob.className = "pt-playhead-knob";
    this.playheadEl.appendChild(knob);

    const lane = document.createElement("div");
    lane.className = "pt-lane";
    lane.appendChild(this.rulerEl);
    lane.appendChild(this.tracksEl);
    lane.appendChild(this.playheadEl);

    this.scrollEl.appendChild(lane);
    body.appendChild(this.labelsEl);
    body.appendChild(this.scrollEl);

    const empty = document.createElement("div");
    empty.className = "pt-empty";
    empty.textContent =
      "Empty timeline. Upload or write a script and watch the layers fill in — subtitles, voices, storyboard, then the final cut.";

    root.appendChild(body);
    root.appendChild(empty);

    this.wireScrub();
    return root;
  }

  private buildToolbar(): HTMLElement {
    const bar = document.createElement("div");
    bar.className = "pt-toolbar";

    const left = document.createElement("div");
    left.className = "pt-tool-group is-left";
    left.appendChild(
      this.iconButton("snap", "⌗", "Snap to grid", () => {
        this.snap = !this.snap;
        this.syncToolStates();
      }),
    );
    left.appendChild(
      this.iconButton("cut", "✂", "Cut tool — click a clip to split", () => {
        this.activeTool = this.activeTool === "cut" ? "select" : "cut";
        this.syncToolStates();
      }),
    );
    left.appendChild(
      this.iconButton("text", "T", "Text tool — click the Text track to add a subtitle", () => {
        this.activeTool = this.activeTool === "text" ? "select" : "text";
        this.syncToolStates();
      }),
    );
    left.appendChild(
      this.iconButton("waveform", "∿", "Toggle waveforms", () => {
        this.showWaveform = !this.showWaveform;
        this.syncToolStates();
        this.render();
      }),
    );
    left.appendChild(this.buildScoreControl());

    const center = document.createElement("div");
    center.className = "pt-transport";
    this.playButton = this.iconButton("play", "▶", "Play / pause", () =>
      this.togglePlayback(),
    );
    this.playButton.classList.add("pt-play");
    this.readoutEl = document.createElement("span");
    this.readoutEl.className = "pt-readout";
    this.readoutEl.dataset.testid = "timeline-readout";
    center.appendChild(this.playButton);
    center.appendChild(this.readoutEl);

    const right = document.createElement("div");
    right.className = "pt-tool-group is-right";
    right.appendChild(
      this.iconButton("zoom-out", "－", "Zoom out", () => this.zoom(1 / ZOOM_FACTOR)),
    );
    right.appendChild(
      this.iconButton("zoom-in", "＋", "Zoom in", () => this.zoom(ZOOM_FACTOR)),
    );
    right.appendChild(
      this.iconButton("grid", "▦", "Toggle grid", () => {
        this.grid = !this.grid;
        this.syncToolStates();
        this.render();
      }),
    );
    right.appendChild(
      this.iconButton("export", "⤓", "Export cut", () => this.exportCut()),
    );

    bar.appendChild(left);
    bar.appendChild(center);
    bar.appendChild(right);
    return bar;
  }

  private iconButton(
    action: string,
    glyph: string,
    title: string,
    onClick: () => void,
  ): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "pt-icon-btn";
    btn.dataset.tool = action;
    btn.dataset.kokoAction = `timeline-${action}`;
    btn.title = title;
    btn.setAttribute("aria-label", title);
    btn.textContent = glyph;
    btn.addEventListener("click", onClick);
    return btn;
  }

  private buildScoreControl(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "pt-score";
    wrap.dataset.testid = "timeline-score";
    this.scoreHost = wrap;
    this.renderScoreControl();
    return wrap;
  }

  private renderScoreControl(): void {
    if (!this.scoreHost) return;
    const score = this.getScore();
    this.scoreHost.replaceChildren();

    const label = document.createElement("span");
    label.className = "pt-score-label";
    label.textContent = "Score";
    this.scoreHost.appendChild(label);

    const select = document.createElement("select");
    select.className = "pt-score-style";
    select.dataset.kokoAction = "timeline-score-style";
    select.title = "Background score style";
    for (const style of Array.from(new Set([score.style, ...SCORE_STYLES]))) {
      const opt = document.createElement("option");
      opt.value = style;
      opt.textContent = style;
      if (style === score.style) opt.selected = true;
      select.appendChild(opt);
    }
    select.addEventListener("change", () => this.setScore({ style: select.value }));
    this.scoreHost.appendChild(select);

    const intensity = document.createElement("input");
    intensity.type = "range";
    intensity.className = "pt-score-intensity";
    intensity.min = "0";
    intensity.max = "100";
    intensity.value = String(score.intensity);
    intensity.title = `Score intensity — ${score.intensity}%`;
    intensity.dataset.kokoAction = "timeline-score-intensity";
    intensity.setAttribute("aria-label", "Score intensity");
    intensity.addEventListener("input", () => {
      const value = Number(intensity.value);
      intensity.title = `Score intensity — ${value}%`;
      this.setScore({ intensity: value });
    });
    this.scoreHost.appendChild(intensity);
  }

  private syncToolStates(): void {
    const map: Record<string, boolean> = {
      snap: this.snap,
      cut: this.activeTool === "cut",
      text: this.activeTool === "text",
      waveform: this.showWaveform,
      grid: this.grid,
    };
    for (const [tool, active] of Object.entries(map)) {
      this.root
        .querySelector<HTMLButtonElement>(`.pt-icon-btn[data-tool="${tool}"]`)
        ?.classList.toggle("is-active", active);
    }
    this.root.dataset.tool = this.activeTool;
  }

  // ------------------------------------------------------------------
  // Background score (Timeline-owned UI; _GLOBAL.md G8)
  // ------------------------------------------------------------------

  private getScore(): BackgroundScore {
    if (this.shim.getBackgroundScore) return this.shim.getBackgroundScore();
    return this.localScore;
  }

  private setScore(patch: Partial<BackgroundScore>): void {
    if (this.shim.setBackgroundScore) {
      this.shim.setBackgroundScore(patch);
      return;
    }
    this.localScore = { ...this.localScore, ...patch };
    this.persistLocalScore();
    this.renderScoreControl();
  }

  private loadLocalScore(): void {
    if (typeof window === "undefined" || !window.localStorage) return;
    const raw = window.localStorage.getItem(LOCAL_SCORE_KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as Partial<BackgroundScore>;
      this.localScore = { ...this.localScore, ...parsed };
    } catch {
      /* corrupt — keep default */
    }
  }

  private persistLocalScore(): void {
    if (typeof window === "undefined" || !window.localStorage) return;
    try {
      window.localStorage.setItem(LOCAL_SCORE_KEY, JSON.stringify(this.localScore));
    } catch {
      /* quota — ignore */
    }
  }

  // ------------------------------------------------------------------
  // Derivation — the living build-up
  // ------------------------------------------------------------------

  private derive(): TimelineModel {
    const scenes = this.store.get("scenes");
    const characters = this.store.get("characters");
    const framesByScene = this.store.get("storyboardFramesBySceneId");
    const subtitle: ClipBlock[] = [];
    const audio: ClipBlock[] = [];
    const main: ClipBlock[] = [];
    const sfx: ClipBlock[] = [];

    const hasVoice = (characterId: string | null): boolean => {
      if (!characterId) return false;
      const ch = characters.find((c) => c.id === characterId) as
        | { voiceProfile?: { design?: string; clonedVoiceId?: string } }
        | undefined;
      const vp = ch?.voiceProfile;
      return !!vp && (!!vp.design || !!vp.clonedVoiceId);
    };

    let t = 0;
    scenes.forEach((scene, sceneIdx) => {
      const lines = this.dialogueLines(scene);
      const sceneStart = t;

      // Subtitle + (gated) audio clips, sequential within the scene.
      let lineT = sceneStart;
      lines.forEach((line, i) => {
        const dur = clamp(line.text.length / 13, 1.2, 5);
        subtitle.push({
          id: `der_subtitle_${scene.id}_${i}`,
          startSec: lineT,
          durationSec: dur,
          label: line.text,
          sceneId: scene.id,
          frameId: null,
          kind: "subtitle",
          ready: true,
          image: null,
          jumpTo: "script_editor",
        });
        // Audio appears once a voice is designed for the speaker, or the
        // scene's line has been generated (defensive across store versions).
        if (hasVoice(line.characterId) || scene.generatedVoiceLine) {
          audio.push({
            id: `der_audio_${scene.id}_${i}`,
            startSec: lineT,
            durationSec: dur,
            label: line.speaker ? `${line.speaker}: ${line.text}` : line.text,
            sceneId: scene.id,
            frameId: null,
            kind: "audio",
            ready: true,
            image: null,
            jumpTo: "characters",
          });
        }
        lineT += dur;
      });
      const dialogueTotal = lineT - sceneStart;

      const frames = (framesByScene[scene.id] ?? [])
        .slice()
        .sort((a, b) => a.order - b.order);
      const frameTotal = frames.length * 2.6;
      const sceneDur = Math.max(dialogueTotal, frameTotal, 3);

      // Main filmstrip: one block per storyboard frame, else a single
      // block once boarded / rendered.
      if (frames.length > 0) {
        const w = sceneDur / frames.length;
        frames.forEach((frame, i) => {
          main.push({
            id: `der_video_${scene.id}_${i}`,
            startSec: sceneStart + i * w,
            durationSec: w,
            label: frame.caption || `${scene.heading} · shot ${i + 1}`,
            sceneId: scene.id,
            frameId: frame.id,
            kind: "video",
            ready: !!scene.videoReady,
            image: frame.image ?? null,
            jumpTo: scene.videoReady ? "final_video" : "storyboard",
          });
        });
      } else if (scene.storyboardReady || scene.videoReady) {
        main.push({
          id: `der_video_${scene.id}_0`,
          startSec: sceneStart,
          durationSec: sceneDur,
          label: scene.heading || `Scene ${sceneIdx + 1}`,
          sceneId: scene.id,
          frameId: null,
          kind: "video",
          ready: !!scene.videoReady,
          image: null,
          jumpTo: scene.videoReady ? "final_video" : "storyboard",
        });
      }

      // A light SFX accent at each scene boundary (a "between-beats" hit).
      if (sceneIdx > 0) {
        sfx.push({
          id: `der_sfx_${scene.id}`,
          startSec: sceneStart,
          durationSec: Math.min(0.8, sceneDur),
          label: `transition → ${scene.heading || `scene ${sceneIdx + 1}`}`,
          sceneId: scene.id,
          frameId: null,
          kind: "sfx",
          ready: true,
          image: null,
          jumpTo: "script_editor",
        });
      }

      t = sceneStart + sceneDur;
    });

    const byTrack: Record<TrackId, ClipBlock[]> = {
      text: [...subtitle, ...this.manualTextBlocks],
      sfx,
      main,
      audio,
    };

    const tracks: TrackModel[] = TRACK_ORDER.map((meta) => ({
      id: meta.id,
      label: meta.label,
      hasVolume: meta.hasVolume,
      waveform: meta.waveform,
      blocks: this.applyOverlays(byTrack[meta.id]),
    }));

    const durationSec = Math.max(
      t,
      ...tracks.flatMap((tr) => tr.blocks.map((b) => b.startSec + b.durationSec)),
      0,
    );
    return { tracks, durationSec };
  }

  /** Apply trim + delete + cut(split) overlays to a track's blocks. */
  private applyOverlays(blocks: ClipBlock[]): ClipBlock[] {
    const out: ClipBlock[] = [];
    for (const raw of blocks) {
      if (this.deletedClips.has(raw.id)) continue;
      const trim = this.trimOverrides.get(raw.id);
      const block: ClipBlock = trim
        ? { ...raw, startSec: trim.start, durationSec: trim.dur }
        : raw;

      const splits = this.splitPoints.get(raw.id);
      if (!splits || splits.length === 0) {
        out.push(block);
        continue;
      }
      const points = Array.from(
        new Set(
          splits.filter(
            (p) => p > block.startSec && p < block.startSec + block.durationSec,
          ),
        ),
      ).sort((a, b) => a - b);
      let segStart = block.startSec;
      [...points, block.startSec + block.durationSec].forEach((edge, idx) => {
        out.push({
          ...block,
          id: `${block.id}__s${idx}`,
          startSec: segStart,
          durationSec: edge - segStart,
        });
        segStart = edge;
      });
    }
    return out;
  }

  private dialogueLines(
    scene: StudioScene,
  ): Array<{ text: string; speaker: string | null; characterId: string | null }> {
    const lines: Array<{ text: string; speaker: string | null; characterId: string | null }> = [];

    if (scene.elements && scene.elements.length > 0) {
      let speaker: string | null = null;
      let speakerId: string | null = null;
      for (const el of scene.elements) {
        if (el.type === "character") {
          speaker = el.text.trim();
          speakerId = el.characterId ?? this.lookupCharacterId(el.text);
        } else if (el.type === "dialogue") {
          const text = el.text.trim();
          if (text) lines.push({ text, speaker, characterId: el.characterId ?? speakerId });
        }
      }
      if (lines.length > 0) return lines;
    }

    // Fallback: parse the flat body. ALL-CAPS short lines are character
    // cues; slug/transition/parenthetical lines are skipped; the rest are
    // dialogue/action beats.
    let speaker: string | null = null;
    let speakerId: string | null = null;
    for (const raw of scene.body.split("\n")) {
      const text = raw.trim();
      if (!text || text === scene.heading) continue;
      if (/^(INT|EXT|INT\/EXT)[.\s]/i.test(text)) continue;
      if (/^[A-Z][A-Z\s]*(TO|IN|OUT):$/.test(text)) continue;
      if (/^\(.*\)$/.test(text)) continue;
      const isCue = /^[A-Z][A-Z0-9 .'-]{1,28}$/.test(text);
      if (isCue) {
        speaker = text;
        speakerId = this.lookupCharacterId(text);
        continue;
      }
      lines.push({ text, speaker, characterId: speakerId });
    }
    return lines;
  }

  private lookupCharacterId(name: string): string | null {
    const key = name.trim().toLowerCase();
    return (
      this.store.get("characters").find((c) => c.name.trim().toLowerCase() === key)?.id ??
      null
    );
  }

  // ------------------------------------------------------------------
  // Store sync (share the cut with Final Video)
  // ------------------------------------------------------------------

  /**
   * Mirror the derived VIDEO clips into `store.clips` (deterministic
   * `der_` ids) so Final Video plays back the same arrangement. Foreign
   * clips (no `der_` prefix) are preserved. No-op when the store predates
   * the shared clip model. Guarded against feedback loops.
   */
  private syncStoreClips(): void {
    if (this.syncing || !this.shim.setClips) return;
    const model = this.derive();
    const mainTrack = model.tracks.find((t) => t.id === "main");
    if (!mainTrack) return;
    const derived: StudioClip[] = mainTrack.blocks
      .filter((b) => b.id.startsWith("der_"))
      .map((b) => ({
        id: b.id,
        sceneId: b.sceneId,
        frameId: b.frameId,
        startSec: round(b.startSec),
        durationSec: round(b.durationSec),
        type: "video" as const,
        content: { image: b.image },
        ready: b.ready,
      }));

    let existing: StudioClip[] = [];
    try {
      existing = (this.store as unknown as { get(k: string): StudioClip[] }).get("clips") ?? [];
    } catch {
      existing = [];
    }
    const foreign = existing.filter((c) => !c.id.startsWith("der_"));
    const currentDerived = existing.filter((c) => c.id.startsWith("der_"));
    if (clipsEqual(currentDerived, derived)) return;

    this.syncing = true;
    try {
      this.shim.setClips([...foreign, ...derived]);
    } finally {
      this.syncing = false;
    }
  }

  // ------------------------------------------------------------------
  // Rendering
  // ------------------------------------------------------------------

  private render(): void {
    this.model = this.derive();
    const { tracks, durationSec } = this.model;
    const totalBlocks = tracks.reduce((n, t) => n + t.blocks.length, 0);

    this.root.classList.toggle("is-empty", totalBlocks === 0 && durationSec === 0);
    this.syncToolStates();
    this.updateReadout();

    const width = Math.max(
      Math.ceil((durationSec + 1) * this.pxPerSec),
      this.scrollEl.clientWidth || 600,
    );

    this.renderRuler(width, durationSec);
    this.renderLabels(tracks);
    this.renderTracks(tracks, width);
    this.updatePlayhead();
  }

  private renderRuler(width: number, durationSec: number): void {
    this.rulerEl.replaceChildren();
    this.rulerEl.style.width = `${width}px`;
    const step = this.tickStep();
    const end = Math.max(durationSec, (this.scrollEl.clientWidth || 600) / this.pxPerSec);
    for (let s = 0; s <= end + step; s += step) {
      const tick = document.createElement("div");
      tick.className = "pt-tick";
      tick.style.left = `${s * this.pxPerSec}px`;
      const label = document.createElement("span");
      label.className = "pt-tick-label";
      label.textContent = `${Math.round(s)}s`;
      tick.appendChild(label);
      this.rulerEl.appendChild(tick);
    }
  }

  private renderLabels(tracks: TrackModel[]): void {
    this.labelsEl.replaceChildren();
    const spacer = document.createElement("div");
    spacer.className = "pt-label-spacer";
    this.labelsEl.appendChild(spacer);

    for (const track of tracks) {
      const row = document.createElement("div");
      row.className = "pt-label";
      row.dataset.track = track.id;

      const name = document.createElement("span");
      name.className = "pt-label-name";
      name.textContent = track.label;
      row.appendChild(name);

      if (track.hasVolume) {
        const mute = document.createElement("button");
        mute.type = "button";
        mute.className = "pt-mute";
        mute.dataset.kokoAction = "timeline-mute";
        const muted = this.mutedTracks.has(track.id);
        mute.classList.toggle("is-muted", muted);
        mute.textContent = muted ? "🔇" : "🔊";
        mute.title = muted ? "Unmute" : "Mute";
        mute.addEventListener("click", () => {
          if (this.mutedTracks.has(track.id)) this.mutedTracks.delete(track.id);
          else this.mutedTracks.add(track.id);
          this.render();
        });
        row.appendChild(mute);

        const vol = document.createElement("input");
        vol.type = "range";
        vol.className = "pt-volume";
        vol.min = "0";
        vol.max = "100";
        vol.value = String(this.trackVolume.get(track.id) ?? 80);
        vol.title = "Track volume";
        vol.setAttribute("aria-label", `${track.label} volume`);
        vol.addEventListener("input", () => {
          this.trackVolume.set(track.id, Number(vol.value));
        });
        row.appendChild(vol);
      }
      this.labelsEl.appendChild(row);
    }
  }

  private renderTracks(tracks: TrackModel[], width: number): void {
    this.tracksEl.replaceChildren();
    this.tracksEl.style.width = `${width}px`;
    this.tracksEl.classList.toggle("show-grid", this.grid);
    if (this.grid) {
      this.tracksEl.style.setProperty(
        "--pt-grid-step",
        `${this.tickStep() * this.pxPerSec}px`,
      );
    }

    for (const track of tracks) {
      const lane = document.createElement("div");
      lane.className = "pt-track";
      lane.dataset.track = track.id;
      lane.classList.toggle("is-muted", this.mutedTracks.has(track.id));

      lane.addEventListener("click", (event) => {
        if (this.activeTool !== "text" || track.id !== "text") return;
        if (event.target !== lane) return;
        this.addManualText(this.eventSec(event, lane));
      });

      for (const block of track.blocks) {
        lane.appendChild(this.buildClipEl(block, track));
      }
      this.tracksEl.appendChild(lane);
    }
  }

  private buildClipEl(block: ClipBlock, track: TrackModel): HTMLElement {
    const el = document.createElement("button");
    el.type = "button";
    el.className = "pt-clip";
    el.dataset.kind = block.kind;
    el.dataset.kokoAction = "timeline-clip";
    el.dataset.clipId = block.id;
    if (block.sceneId) el.dataset.sceneId = block.sceneId;
    el.classList.toggle("is-ready", block.ready);
    el.classList.toggle("is-selected", this.selectedClipId === block.id);
    el.style.left = `${block.startSec * this.pxPerSec}px`;
    el.style.width = `${Math.max(block.durationSec * this.pxPerSec - 2, 6)}px`;
    el.title = `${block.label} — click to edit`;

    if (block.kind === "video") {
      const film = document.createElement("div");
      film.className = "pt-filmstrip";
      const cells = Math.max(1, Math.round((block.durationSec * this.pxPerSec) / 46));
      for (let i = 0; i < Math.min(cells, 12); i++) {
        const cell = document.createElement("div");
        cell.className = "pt-frame";
        if (block.image) cell.style.backgroundImage = `url("${block.image}")`;
        film.appendChild(cell);
      }
      el.appendChild(film);
    } else if (track.waveform && this.showWaveform) {
      el.appendChild(this.buildWaveform(block));
    }

    if (block.kind !== "video") {
      const caption = document.createElement("span");
      caption.className = "pt-clip-label";
      caption.textContent = block.label;
      el.appendChild(caption);
    }

    el.appendChild(this.buildTrimHandle(block, "left"));
    el.appendChild(this.buildTrimHandle(block, "right"));

    const del = document.createElement("span");
    del.className = "pt-clip-del";
    del.dataset.kokoAction = "timeline-delete";
    del.textContent = "×";
    del.title = "Delete clip";
    del.addEventListener("click", (event) => {
      event.stopPropagation();
      this.deletedClips.add(block.id.split("__s")[0]);
      if (this.selectedClipId === block.id) this.selectedClipId = null;
      this.syncStoreClips();
      this.render();
    });
    el.appendChild(del);

    el.addEventListener("click", (event) => {
      if (this.activeTool === "cut") {
        this.cutClipAt(block, this.eventSec(event, el.parentElement as HTMLElement));
        return;
      }
      this.selectClip(block);
    });
    return el;
  }

  private buildWaveform(block: ClipBlock): HTMLElement {
    const wave = document.createElement("div");
    wave.className = "pt-wave";
    const seedBase = hashString(block.id);
    const bars = Math.max(6, Math.round((block.durationSec * this.pxPerSec) / 5));
    for (let i = 0; i < Math.min(bars, 160); i++) {
      const bar = document.createElement("div");
      bar.className = "pt-wave-bar";
      const h = 24 + Math.abs((Math.sin((seedBase + i) * 12.9898) * 1000) % 1) * 64;
      bar.style.height = `${h}%`;
      wave.appendChild(bar);
    }
    return wave;
  }

  private buildTrimHandle(block: ClipBlock, side: "left" | "right"): HTMLElement {
    const handle = document.createElement("span");
    handle.className = `pt-trim pt-trim-${side}`;
    handle.dataset.kokoAction = "timeline-trim";
    handle.title = "Trim";
    handle.addEventListener("pointerdown", (event) => {
      event.stopPropagation();
      this.beginTrim(block, side, event, handle);
    });
    handle.addEventListener("click", (e) => e.stopPropagation());
    return handle;
  }

  // ------------------------------------------------------------------
  // Interactions
  // ------------------------------------------------------------------

  private selectClip(block: ClipBlock): void {
    this.selectedClipId = block.id;
    this.render();
    // Click-to-edit: jump to the clip's source tool. The dock is built
    // before the PanelManager exists in main.ts, so fall back to the
    // already-exposed `window.__studioPanelManager` hook when needed.
    const pm =
      this.panelManager ??
      (window as unknown as { __studioPanelManager?: PanelManager }).__studioPanelManager;
    if (pm && block.sceneId) {
      this.store.setActiveScene(block.sceneId);
      pm.open(block.jumpTo);
    }
  }

  private cutClipAt(block: ClipBlock, sec: number): void {
    const baseId = block.id.split("__s")[0];
    const point = this.maybeSnap(sec);
    const existing = this.splitPoints.get(baseId) ?? [];
    this.splitPoints.set(baseId, [...existing, point]);
    this.render();
  }

  private beginTrim(
    block: ClipBlock,
    side: "left" | "right",
    startEvent: PointerEvent,
    handle: HTMLElement,
  ): void {
    const baseId = block.id.split("__s")[0];
    const startX = startEvent.clientX;
    const origStart = block.startSec;
    const origDur = block.durationSec;
    try {
      handle.setPointerCapture(startEvent.pointerId);
    } catch {
      /* ignore */
    }

    const onMove = (event: PointerEvent): void => {
      const deltaSec = (event.clientX - startX) / this.pxPerSec;
      let nextStart = origStart;
      let nextDur = origDur;
      if (side === "left") {
        nextStart = clamp(origStart + deltaSec, 0, origStart + origDur - 0.3);
        nextDur = origStart + origDur - nextStart;
      } else {
        nextDur = Math.max(0.3, origDur + deltaSec);
      }
      this.trimOverrides.set(baseId, {
        start: this.maybeSnap(nextStart),
        dur: round(nextDur),
      });
      this.render();
    };
    const onUp = (event: PointerEvent): void => {
      try {
        handle.releasePointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
      handle.removeEventListener("pointermove", onMove);
      handle.removeEventListener("pointerup", onUp);
      this.syncStoreClips();
    };
    handle.addEventListener("pointermove", onMove);
    handle.addEventListener("pointerup", onUp);
  }

  private addManualText(sec: number): void {
    const text = window.prompt("Subtitle text", "New subtitle");
    if (!text || !text.trim()) return;
    this.manualTextBlocks.push({
      id: `manual_text_${Date.now().toString(36)}`,
      startSec: this.maybeSnap(Math.max(0, sec)),
      durationSec: 2.5,
      label: text.trim(),
      sceneId: null,
      frameId: null,
      kind: "subtitle",
      ready: true,
      image: null,
      jumpTo: "script_editor",
    });
    this.render();
  }

  private eventSec(event: MouseEvent, lane: HTMLElement): number {
    const rect = lane.getBoundingClientRect();
    return Math.max(0, (event.clientX - rect.left) / this.pxPerSec);
  }

  private maybeSnap(sec: number): number {
    if (!this.snap) return Math.max(0, sec);
    return Math.max(0, Math.round(sec / SNAP_SEC) * SNAP_SEC);
  }

  private zoom(factor: number): void {
    this.pxPerSec = clamp(this.pxPerSec * factor, MIN_PX_PER_SEC, MAX_PX_PER_SEC);
    this.render();
  }

  private tickStep(): number {
    const target = 64 / this.pxPerSec;
    const candidates = [0.5, 1, 2, 5, 10, 15, 30, 60];
    return candidates.find((c) => c >= target) ?? 60;
  }

  // ------------------------------------------------------------------
  // Playback + scrub
  // ------------------------------------------------------------------

  private togglePlayback(): void {
    if (this.playing) this.stopPlayback();
    else this.startPlayback();
  }

  private startPlayback(): void {
    if (this.model.durationSec <= 0) return;
    this.playing = true;
    this.playButton.textContent = "⏸";
    this.playButton.classList.add("is-playing");
    this.lastFrameTs = performance.now();
    const tick = (ts: number): void => {
      if (!this.playing) return;
      const dt = (ts - this.lastFrameTs) / 1000;
      this.lastFrameTs = ts;
      this.playheadSec += dt;
      if (this.playheadSec >= this.model.durationSec) this.playheadSec = 0;
      this.updatePlayhead();
      this.updateReadout();
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  private stopPlayback(): void {
    this.playing = false;
    if (this.playButton) {
      this.playButton.textContent = "▶";
      this.playButton.classList.remove("is-playing");
    }
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private wireScrub(): void {
    let scrubbing = false;
    const toSec = (clientX: number): number => {
      const rect = this.rulerEl.getBoundingClientRect();
      return this.maybeSnap(Math.max(0, (clientX - rect.left) / this.pxPerSec));
    };
    this.rulerEl.addEventListener("pointerdown", (event) => {
      scrubbing = true;
      try {
        this.rulerEl.setPointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
      this.playheadSec = toSec(event.clientX);
      this.updatePlayhead();
      this.updateReadout();
    });
    this.rulerEl.addEventListener("pointermove", (event) => {
      if (!scrubbing) return;
      this.playheadSec = toSec(event.clientX);
      this.updatePlayhead();
      this.updateReadout();
    });
    const end = (event: PointerEvent): void => {
      scrubbing = false;
      try {
        this.rulerEl.releasePointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
    };
    this.rulerEl.addEventListener("pointerup", end);
    this.rulerEl.addEventListener("pointercancel", end);
  }

  private updatePlayhead(): void {
    this.playheadEl.style.left = `${this.playheadSec * this.pxPerSec}px`;
  }

  private updateReadout(): void {
    this.readoutEl.textContent = `${fmtTime(this.playheadSec)} / ${fmtTime(
      this.model.durationSec,
    )}`;
  }

  // ------------------------------------------------------------------
  // Export
  // ------------------------------------------------------------------

  /**
   * Export the current arrangement as a real, self-contained editable
   * cut (JSON). This is the stubbed render/bundle hook — the structure is
   * a true exportable cut a downstream renderer could consume.
   */
  private exportCut(): void {
    const cut = {
      kind: "koko-studio-cut",
      version: 1,
      project: this.store.getProjectTitle(),
      exportedAt: new Date().toISOString(),
      durationSec: round(this.model.durationSec),
      backgroundScore: this.getScore(),
      tracks: this.model.tracks.map((track) => ({
        id: track.id,
        label: track.label,
        muted: this.mutedTracks.has(track.id),
        volume: this.trackVolume.get(track.id) ?? 100,
        clips: track.blocks.map((b) => ({
          id: b.id,
          start: round(b.startSec),
          duration: round(b.durationSec),
          kind: b.kind,
          sceneId: b.sceneId,
          label: b.label,
        })),
      })),
    };
    const json = JSON.stringify(cut, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${slugify(cut.project)}-cut.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);

    this.root.dataset.lastExport = String(Date.now());
    const btn = this.root.querySelector<HTMLButtonElement>(
      '.pt-icon-btn[data-tool="export"]',
    );
    if (btn) {
      btn.classList.add("is-flashing");
      setTimeout(() => btn.classList.remove("is-flashing"), 600);
    }
  }
}

// --------------------------------------------------------------------
// Pure helpers
// --------------------------------------------------------------------

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}

function fmtTime(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
}

function slugify(text: string): string {
  return (
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "untitled"
  );
}

function hashString(text: string): number {
  let h = 0;
  for (let i = 0; i < text.length; i++) h = (h * 31 + text.charCodeAt(i)) % 100000;
  return h;
}

function clipsEqual(a: StudioClip[], b: StudioClip[]): boolean {
  if (a.length !== b.length) return false;
  const key = (c: StudioClip): string =>
    `${c.id}|${c.startSec}|${c.durationSec}|${c.ready ? 1 : 0}|${c.frameId ?? ""}`;
  const sa = a.map(key).sort();
  const sb = b.map(key).sort();
  return sa.every((v, i) => v === sb[i]);
}
